/**
 * User-facing copy for org-console authentication errors.
 * Maps API/OAuth machine codes and technical client messages to plain language.
 */

export const ORG_AUTH_GENERIC_FAILURE = "Sign-in failed. Please try again or contact support." as const;

export const ORG_AUTH_CREDENTIALS_FAILURE =
    "We couldn't sign you in with that email and password. Check both fields, then try again." as const;

/**
 * Copy for a 429 from an auth endpoint. When the backend body carries
 * `retryAfterSec` the message names the actual wait; older backends without
 * the field fall back to "a few minutes".
 */
export function formatOrgAuthRateLimitMessage(retryAfterSec?: number | null): string {
    if (retryAfterSec === undefined || retryAfterSec === null || retryAfterSec <= 0) {
        return "Too many attempts. Wait a few minutes, then try again.";
    }
    const minutes = Math.ceil(retryAfterSec / 60);
    return minutes <= 1
        ? "Too many attempts. Try again in about a minute."
        : `Too many attempts. Try again in about ${minutes} minutes.`;
}

/**
 * Thrown for auth-endpoint 429s so pages can branch on the error type
 * (instead of string-matching copy) while `message` stays user-facing.
 */
export class OrgAuthRateLimitError extends Error {
    readonly retryAfterSec: number | null;

    constructor(retryAfterSec?: number | null) {
        super(formatOrgAuthRateLimitMessage(retryAfterSec));
        this.name = "OrgAuthRateLimitError";
        this.retryAfterSec = retryAfterSec ?? null;
    }
}

/**
 * How a console names the two sides of an issuer-client relationship in auth
 * and operating-context copy. The defaults keep the original Issuer / Client
 * wording; the YaaS Distributor and Company Recipient consoles install
 * `ORG_AUTH_DISTRIBUTOR_CONSOLE_TERMS` at startup via `setOrgAuthConsoleTerms`.
 */
export interface OrgAuthConsoleTerms {
    /** Lowercase noun for the issuing side ("issuer"). */
    issuer: string;
    /** Lowercase noun for the receiving side ("client"). */
    client: string;
    /** Lowercase noun phrase for the relationship ("issuer-client relationship"). */
    relationship: string;
    /** Title Case name of the issuing side's console ("Issuer Console"). */
    issuerConsole: string;
    /** Title Case name of the receiving side's console ("Client Console"). */
    clientConsole: string;
}

export const DEFAULT_ORG_AUTH_CONSOLE_TERMS: OrgAuthConsoleTerms = {
    issuer: "issuer",
    client: "client",
    relationship: "issuer-client relationship",
    issuerConsole: "Issuer Console",
    clientConsole: "Client Console"
};

export const ORG_AUTH_DISTRIBUTOR_CONSOLE_TERMS: OrgAuthConsoleTerms = {
    issuer: "distributor",
    client: "company recipient",
    relationship: "distributor relationship",
    issuerConsole: "Distributor Console",
    clientConsole: "Company Recipient Console"
};

let activeConsoleTerms: OrgAuthConsoleTerms = DEFAULT_ORG_AUTH_CONSOLE_TERMS;

/** Install the console's wording for auth copy. Call once at app startup. */
export function setOrgAuthConsoleTerms(terms: OrgAuthConsoleTerms): void {
    activeConsoleTerms = terms;
}

function capitalizeFirst(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Codes and messages whose copy names the issuer / client roles. */
function consoleTermMessages(terms: OrgAuthConsoleTerms): {
    byCode: Record<string, string>;
    byMessage: Record<string, string>;
} {
    const capabilitySuspended = `${capitalizeFirst(terms.issuer)} access for this organization is suspended. You can view existing records, but changes are disabled.`;
    const contextRequired = `Choose which ${terms.issuer} relationship to use before continuing. This ${terms.client} account has more than one ${terms.issuer} context.`;
    const contextInvalid = `The selected ${terms.issuer} relationship is no longer available. Choose another ${terms.issuer} context and try again.`;
    const readOnly = `This ${terms.relationship} is read-only. Changes are disabled until the relationship is reactivated.`;
    const suspended = `This ${terms.relationship} is suspended. You can view existing records, but changes are disabled until it is reactivated.`;
    const cancelled = `This ${terms.relationship} has been canceled. You can view history, but changes are disabled.`;

    return {
        byCode: {
            WRONG_CONSOLE: `This account belongs to a different console. Open the ${terms.clientConsole} or ${terms.issuerConsole} that matches your invite.`,
            ISSUER_CAPABILITY_REQUIRED: `This organization does not have ${terms.issuer} access. Use the ${terms.clientConsole} or contact support.`,
            ISSUER_CAPABILITY_SUSPENDED: capabilitySuspended,
            CLIENT_OPERATING_CONTEXT_REQUIRED: contextRequired,
            CLIENT_OPERATING_CONTEXT_INVALID: contextInvalid,
            OPERATING_CONTEXT_READ_ONLY: readOnly,
            IssuerCapabilitySuspended: capabilitySuspended,
            ISSUER_CLIENT_SUSPENDED: suspended,
            IssuerClientSuspended: suspended,
            CLIENT_RELATIONSHIP_SUSPENDED: suspended,
            ISSUER_CLIENT_CANCELLED: cancelled,
            ISSUER_CLIENT_CANCELED: cancelled,
            IssuerClientCancelled: cancelled,
            CLIENT_RELATIONSHIP_CANCELLED: cancelled,
            CLIENT_RELATIONSHIP_CANCELED: cancelled
        },
        // API messages matched verbatim; the keys are yaas-api wording, not copy.
        byMessage: {
            "Issuer capability is suspended": capabilitySuspended,
            "Client operating context required": contextRequired,
            "Client operating context invalid": contextInvalid,
            "Client operating context is read-only": readOnly,
            IssuerCapabilitySuspended: capabilitySuspended,
            IssuerClientSuspended: suspended,
            IssuerClientCancelled: cancelled
        }
    };
}

const ORG_AUTH_HARD_COMPLIANCE_REQUIREMENTS_INCOMPLETE =
    "Complete the required compliance steps before making this change." as const;

export interface ResolveOrgAuthMessageOptions {
    /** Machine-readable code (`error` field, `auth_error` query param, OAuth `error`). */
    code?: string | null;
    /** Human or technical message (`message` field, thrown Error.message, `error_description`). */
    message?: string | null;
    /** The console's own name (e.g. "Issuer Console") — refines NO_ORG_MEMBERSHIP copy. */
    appLabel?: string;
}

function getConsoleRoleLabel(appLabel?: string): string {
    const normalized = appLabel?.toLowerCase() ?? "";
    const terms = activeConsoleTerms;
    if (normalized.includes(terms.issuerConsole.toLowerCase())) {
        return terms.issuer;
    }
    if (normalized.includes(terms.clientConsole.toLowerCase())) {
        return terms.client;
    }
    if (normalized.includes("issuer")) {
        return "issuer";
    }
    if (normalized.includes("client")) {
        return "client";
    }
    return "issuer or client";
}

/** Known API / policy error codes from yaas-api org auth. */
const AUTH_ERROR_BY_CODE: Record<string, string> = {
    INVALID_CREDENTIALS: ORG_AUTH_CREDENTIALS_FAILURE,
    INVALID_EMAIL_OR_PASSWORD: ORG_AUTH_CREDENTIALS_FAILURE,
    EMAIL_NOT_VERIFIED:
        "Your email address isn't verified with your sign-in provider (for example, Google). Verify it there, then try again.",
    EXTERNAL_AUTH_DISABLED: "This sign-in method is not enabled. Please use another sign-in option or contact support.",
    EXTERNAL_AUTH_FAILED: "Sign-in didn't complete. Please try again.",
    EXTERNAL_IDENTITY_NOT_LINKED:
        "This account isn't linked to a member of your organization. Sign in with the email you were invited with, or ask your administrator for an invite.",
    EXTERNAL_IDENTITY_REVOKED:
        "This sign-in method has been disabled for your account. Please contact your administrator or support.",
    OIDC_PROVIDER_NOT_FOUND:
        "Your organization's SSO connection is no longer active. Please contact your administrator.",
    ORG_INACTIVE: "Your organization is currently inactive. Please contact support.",
    ORG_USER_NOT_FOUND:
        "No account was found for this email. Use the email you were invited with, or ask your organization administrator for an invite.",
    SSO_DOMAIN_MISMATCH:
        "That email doesn't match your organization's sign-in setup. Use your work email, or contact your administrator if this email should have access.",
    SSO_NOT_CONFIGURED:
        "SSO isn't set up for this email or organization. Try another sign-in option or contact your administrator.",
    SSO_PROVIDER_MISMATCH:
        "This organization uses a different SSO provider. Use the SSO option from your organization or contact your administrator.",
    SSO_REQUIRED: "This organization requires SSO. Please use the SSO sign-in option.",
    // WRONG_CONSOLE and the issuer capability / client operating context
    // codes name the console roles: see consoleTermMessages.

    // Compliance hard gates
    HARD_REQUIREMENT_UNSATISFIED: ORG_AUTH_HARD_COMPLIANCE_REQUIREMENTS_INCOMPLETE,
    hard_requirement_unsatisfied: ORG_AUTH_HARD_COMPLIANCE_REQUIREMENTS_INCOMPLETE,
    COMPLIANCE_HARD_REQUIREMENTS_INCOMPLETE: ORG_AUTH_HARD_COMPLIANCE_REQUIREMENTS_INCOMPLETE,
    HARD_COMPLIANCE_REQUIREMENTS_INCOMPLETE: ORG_AUTH_HARD_COMPLIANCE_REQUIREMENTS_INCOMPLETE,

    // Console policy (consoleConfig.ts)
    NO_ORG_MEMBERSHIP: "", // resolved via appLabel below

    // OAuth authorize / client misconfiguration
    "Unknown client_id": "This console is not configured correctly. Please contact support.",
    "redirect_uri not registered for this client": "This console is not configured correctly. Please contact support.",
    "Invalid external auth state": "Your sign-in session expired. Please try again.",
    INVALID_EXTERNAL_AUTH_STATE: "Your sign-in session expired. Please try again.",
    "Internal server error": "Something went wrong on our side. Please try again in a moment.",

    // Standard OAuth 2.0 error codes (callback ?error=)
    access_denied: "Sign-in was canceled. Please try again when you're ready.",
    invalid_request: "Sign-in could not be completed. Please try again.",
    invalid_scope: "Sign-in could not be completed. Please try again.",
    invalid_client: "This console is not configured correctly. Please contact support.",
    unauthorized_client: "This console is not allowed to sign in. Please contact support.",
    unsupported_response_type: "Sign-in could not be completed. Please try again.",
    server_error: "Something went wrong during sign-in. Please try again.",
    temporarily_unavailable: "Sign-in is temporarily unavailable. Please try again shortly.",
    invalid_grant: "Your sign-in session expired or was already used. Please try again."
};

/** Technical messages thrown in the browser (PKCE, navigation guards, oauth-react). */
const AUTH_ERROR_BY_MESSAGE: Record<string, string> = {
    "Invalid email or password": ORG_AUTH_CREDENTIALS_FAILURE,
    "Invalid email or password.": ORG_AUTH_CREDENTIALS_FAILURE,
    "Session expired. Please log in again.": "Your sign-in session expired. Please try again.",
    "State mismatch \u2014 possible CSRF attack.": "Sign-in could not be verified. Please try again.",
    "Authorization code was already processed. Please log in again.":
        "This sign-in link was already used. Please try again.",
    "Browser storage is not available in this environment.":
        "Sign-in isn't available in this browser window. Try again outside private or incognito mode.",
    "Login failed. Please try again.": ORG_AUTH_GENERIC_FAILURE,
    "Unsupported external login provider": ORG_AUTH_GENERIC_FAILURE,
    "Authentication failed.": ORG_AUTH_GENERIC_FAILURE,
    "Invalid callback — missing authorization code.": "Sign-in could not be completed. Please try again.",
    "Unexpected redirect hostname — possible response tampering": ORG_AUTH_GENERIC_FAILURE,
    "Unexpected Google redirect hostname — possible response tampering": ORG_AUTH_GENERIC_FAILURE,
    "Unexpected SSO redirect protocol — possible response tampering": ORG_AUTH_GENERIC_FAILURE,
    hard_requirement_unsatisfied: ORG_AUTH_HARD_COMPLIANCE_REQUIREMENTS_INCOMPLETE
};

const AUTH_ERROR_MESSAGE_PATTERNS: ReadonlyArray<{ pattern: RegExp; message: string }> = [
    {
        pattern: /^Unable to reach the authentication server/i,
        message: "We couldn't reach the sign-in service. Check your connection and try again."
    },
    {
        pattern: /^Authentication failed: \d+$/i,
        message: ORG_AUTH_GENERIC_FAILURE
    },
    {
        pattern: /^Google login failed: \d+$/i,
        message: "Google sign-in didn't start. Please try again."
    },
    {
        pattern: /^SSO login failed: \d+$/i,
        message: "SSO sign-in didn't start. Please try again."
    },
    {
        pattern: /^Failed to fetch user profile/i,
        message: "We couldn't load your account after sign-in. Please try again."
    },
    {
        pattern: /PKCE verification failed/i,
        message: "Your sign-in session expired. Please try again."
    },
    {
        pattern: /^Code (not found|already consumed|expired)/i,
        message: "Your sign-in session expired. Please try again."
    },
    {
        pattern: /^Invalid refresh token|^Refresh token (revoked|expired)/i,
        message: "Your session ended. Please sign in again."
    }
];

function normalizeCode(raw: string): string {
    return raw.trim();
}

function looksLikeMachineCode(value: string): boolean {
    const trimmed = value.trim();
    return /^[A-Z][A-Z0-9_]*$/.test(trimmed) && trimmed.includes("_");
}

function resolveNoOrgMembership(appLabel?: string): string {
    return `This account is not registered for the ${getConsoleRoleLabel(appLabel)} console. Open the console that matches your invite, or ask an administrator to invite this account.`;
}

function messageFromCode(code: string, appLabel?: string): string | null {
    const normalized = normalizeCode(code);
    if (normalized === "NO_ORG_MEMBERSHIP") {
        return resolveNoOrgMembership(appLabel);
    }
    const termed = consoleTermMessages(activeConsoleTerms).byCode;
    return (
        termed[normalized] ??
        termed[normalized.toUpperCase()] ??
        AUTH_ERROR_BY_CODE[normalized] ??
        AUTH_ERROR_BY_CODE[normalized.toUpperCase()] ??
        AUTH_ERROR_BY_CODE[normalized.toLowerCase()] ??
        null
    );
}

/**
 * Resolve any auth failure to end-user copy. Returns `null` when there is nothing to show.
 */
export function resolveOrgAuthUserMessage(options: ResolveOrgAuthMessageOptions): string | null {
    const code = options.code?.trim() || null;
    const rawMessage = options.message?.trim() || null;

    if (!code && !rawMessage) {
        return null;
    }

    if (code) {
        const fromCode = messageFromCode(code, options.appLabel);
        if (fromCode) {
            return fromCode;
        }
    }

    if (rawMessage) {
        if (looksLikeMachineCode(rawMessage)) {
            const fromMessageAsCode = messageFromCode(rawMessage, options.appLabel);
            if (fromMessageAsCode) {
                return fromMessageAsCode;
            }
        }

        const exact =
            consoleTermMessages(activeConsoleTerms).byMessage[rawMessage] ?? AUTH_ERROR_BY_MESSAGE[rawMessage];
        if (exact) {
            return exact;
        }

        for (const { pattern, message } of AUTH_ERROR_MESSAGE_PATTERNS) {
            if (pattern.test(rawMessage)) {
                return message;
            }
        }

        // Safe, already-friendly API copy (e.g. "Invalid email or password").
        if (!looksLikeMachineCode(rawMessage) && !rawMessage.includes("Internal server error")) {
            return rawMessage;
        }
    }

    return ORG_AUTH_GENERIC_FAILURE;
}

/** @deprecated Prefer `resolveOrgAuthUserMessage` — kept for call sites using query `auth_error` only. */
export function getOrgAuthErrorMessage(
    errorCode: string | null | undefined,
    options: { appLabel?: string } = {}
): string | null {
    if (!errorCode) {
        return null;
    }
    return resolveOrgAuthUserMessage({ code: errorCode, appLabel: options.appLabel });
}

export function isOrgAuthCredentialsError(message: string | null | undefined): boolean {
    if (!message) {
        return false;
    }

    return resolveOrgAuthUserMessage({ message }) === ORG_AUTH_CREDENTIALS_FAILURE;
}
